import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { Spin } from "antd";
import AddNewCity from "../../modals/AddNewCity";
import DeleteCity from "../../modals/DeleteCity";
import UpdateCity from "../../modals/UpdateCity";
import { useTranslation } from "react-i18next";
import { UTILS } from "utils";
import useCurrentLang from "hooks/useCurrentLang";
import useUserType from "hooks/useUserType";
import RenderTime from "features/shipping_methods/common/RenderTime";
import AppHeading1 from "components/ui/AppHeading1";
import AppSearchInput from "components/ui/AppSearchInput";
import AppPaginatedTable from "components/ui/AppPaginatedTable";
import AppButton from "components/ui/AppButton";
import AppIcon from "components/ui/AppIcon";
function CitiesTable() {
  const { currentLanguage } = useSelector((state) => state.LanguageSlice);
  const { isLoading } = useSelector((state) => state.LoaderReducer);

  const { selectedStore, addedCities } = useSelector(
    (state) => state.AreasReducer
  );

  const userType = useUserType() || UTILS.getCurrentUser()?.userType;
  const [searchTerm, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [dialogContentId, setDialogContentId] = useState(-1);
  const [selectedDataRows, setSelectedDataRows] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const { t } = useTranslation();
  const language = useCurrentLang();

  const columns = [
    {
      key: "cityID",
      title: t("City Name"),
      sorter: (a, b) =>
        a?.cityID?.cityNameEN?.localeCompare(b?.cityID?.cityNameEN),
      dataIndex: "cityName",
      render: (val, row, index) => {
        return language === "en"
          ? row?.cityID?.cityNameEN
          : row?.cityID?.cityNameAR;
      },
    },
    {
      key: "minOrder",
      title: t("Min. Basket Value"),
      dataIndex: "minOrder",
      sorter: {
        compare: (a, b) => a.minOrder - b.minOrder,
        multiple: 3,
      },
      render: (val) => {
        return new Intl.NumberFormat(currentLanguage, {
          style: "currency",
          currency: "AED",
        }).format(val);
      },
    },
    {
      key: "deliveryFee",
      title: t("Delivery Fee"),
      dataIndex: "deliveryFee",
      sorter: {
        compare: (a, b) => a.deliveryFee - b.deliveryFee,
        multiple: 3,
      },
      render: (val) => {
        return new Intl.NumberFormat(currentLanguage, {
          style: "currency",
          currency: "AED",
        }).format(val);
      },
    },

    {
      key: "deliveryTime",
      title: t("Delivery Time"),
      dataIndex: "deliveryTime",
      sorter: {
        compare: (a, b) => a.deliveryTime - b.deliveryTime,
        multiple: 3,
      },
      render: (val, row, index) => {
        return <RenderTime {...{ val, row, index }} />;
      },
    },
    {
      title: t("Actions"),
      render: (_, row) => (
        <div className="flex justify-start gap-3">
          <span onClick={() => editArea(row)}>
            <AppIcon name="edit" className="cursor-pointer" />
          </span>
          <span onClick={() => deleteArea(row)}>
            <AppIcon name="trash" className="cursor-pointer" />
          </span>
        </div>
      ),
    },
  ];

  const DG_CONTENT_ID = {
    ADD_CITY: 0,
    DELETE_CITY: 1,
    UPDATE_CITY: 2,
    ADD_ITEM_TO_CITY: 3,
  };

  const onClose = () => {
    setOpen(false);
    setSelectedDataRows([]);
    setSelectedKeys([]);
  };
  const renderModalContent = () => {
    switch (dialogContentId) {
      case 0:
        return <AddNewCity open={open} onClose={onClose} />;
      case 1:
        return (
          <DeleteCity
            open={open}
            selectedCities={selectedDataRows}
            selectedRow={selectedDataRows}
            onClose={onClose}
          />
        );
      case 2:
        return (
          <UpdateCity
            open={open}
            selectedCities={selectedDataRows}
            onClose={onClose}
          />
        );
      default:
        break;
    }
  };

  const Items = useMemo(() => {
    const lowerCaseSearchTerm = searchTerm?.toLowerCase() ?? "";
    const cityDeliveryData = addedCities?.cityDeliveryData || [];
    if (!lowerCaseSearchTerm) {
      return cityDeliveryData.map((item, index) => ({ ...item, key: index }));
    }

    return cityDeliveryData
      ?.filter(
        (item) =>
        item?.cityID?.cityNameEN
          ?.toLowerCase()
          ?.includes(lowerCaseSearchTerm) ||
        item?.cityID?.cityNameAR?.toLowerCase()?.includes(lowerCaseSearchTerm)
      )
      .map((item, index) => ({ ...item, key: index }));
  }, [addedCities, searchTerm]);

  const handSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const deleteArea = (row) => {
    setSelectedDataRows(row ? [row] : []);
    setOpen(true);
    setDialogContentId(DG_CONTENT_ID.DELETE_CITY);
  };

  // Selection is held as ids against the unfiltered list, so narrowing the search
  // never silently drops a row the user already ticked.
  const selectedRows = (addedCities?.cityDeliveryData || []).filter((row) =>
    selectedKeys.includes(row?._id)
  );

  const deleteSelected = () => {
    if (selectedRows.length === 0) return;
    setSelectedDataRows(selectedRows);
    setOpen(true);
    setDialogContentId(DG_CONTENT_ID.DELETE_CITY);
  };
  const editArea = (row) => {
    setSelectedDataRows(row ? [row] : []);
    setOpen(true);
    setDialogContentId(DG_CONTENT_ID.UPDATE_CITY);
  };
  const addCities = () => {
    setOpen(true);
    setDialogContentId(DG_CONTENT_ID.ADD_CITY);
  };
  const cityCount = addedCities?.cityDeliveryData?.length || 0;

  return (
    <>
      <div>
        <Spin spinning={isLoading}>
          {selectedStore && (
            <>
              {renderModalContent()}
              <div className="my-3 flex flex-col md:flex-row flex-shrink-0 items-center justify-between">
                <AppHeading1>Cities for {selectedStore?.label}</AppHeading1>
                <div className="flex flex-col md:flex-row items-center gap-3">
                  <AppSearchInput
                    value={searchTerm}
                    placeholder={t("search city by name")}
                    onInput={handSearch}
                  />
                  {userType !== "MANAGER" && (
                    <div className="md:mt-0 mt-3 flex gap-3">
                      <AppButton onClick={addCities}>
                        {t("Add Delivery City")}
                      </AppButton>
                    </div>
                  )}
                  {/* Bulk delete follows the same rule as the row delete icon,
                      which is available to every role on every delivery table. */}
                  {selectedKeys.length > 0 && (
                    <div className="md:mt-0 mt-3 flex gap-3">
                      <AppButton variant="danger" onClick={deleteSelected}>
                        {t("Delete Selected")} ({selectedKeys.length})
                      </AppButton>
                      <AppButton
                        variant="secondary"
                        onClick={() => setSelectedKeys([])}
                      >
                        {t("Clear selection")}
                      </AppButton>
                    </div>
                  )}
                </div>
              </div>
              <div className="min-h-0 min-w-0 w-full max-w-full flex-1 bg-white">
                <AppPaginatedTable
                  data={Items}
                  total={Items?.length || 0}
                  page={page}
                  pageSize={pageSize}
                  clientSidePagination
                  onPageChange={(p, ps) => {
                    setPage(p);
                    setPageSize(ps);
                  }}
                  columns={columns}
                  selectable
                  selectedRowKeys={selectedKeys}
                  onSelectionChange={setSelectedKeys}
                />
              </div>
            </>
          )}
        </Spin>
      </div>
    </>
  );
}

export default CitiesTable;
